import * as XLSX from 'xlsx'
import store from '@/store'

export default function useExportARR() {
  const { client } = store.state?.userStore?.userData
  const clientData = JSON.parse(localStorage.getItem("clientData") || '{}')
  const clientName = clientData?.name || client?.name || 'Cliente'

  const exportToExcel = (analyses) => {
    if (!analyses) {
      return
    }

    // Create workbook
    const wb = XLSX.utils.book_new()

    // Add ARR sheet with analyses
    addARRSheet(wb, analyses)

    // Generate filename
    const today = new Date().toISOString().split('T')[0]
    const filename = `ARR_${today}.xlsx`
    XLSX.writeFile(wb, filename)
  }

  const addARRSheet = (wb, analyses) => {
    // Fila 1: Título principal
    const headerRow1 = [
      'ANÁLISIS DE RIESGO RESIDUAL (ARR)', // A1
      ...Array(19).fill('') // B1-T1 (merged with A1)
    ]

    // Fila 2: Secciones principales
    const headerRow2 = [
      'IDENTIFICACIÓN', // A2
      '', // B2 (merged with A2)
      'RIESGO INHERENTE', // C2
      '', '', '', // D2-F2 (merged with C2)
      'CONTROLES', // G2
      '', '', '', // H2-J2 (merged with G2)
      'RIESGO RESIDUAL', // K2
      '', '', '', // L2-N2 (merged with K2)
      'REDUCCIÓN', // O2
      'PLAN DE ACCIÓN', // P2
      '', '', '', '' // Q2-T2 (merged with P2)
    ]

    // Fila 3: Títulos individuales de cada columna
    const headerRow3 = [
      'ID', // A3
      'TRATAMIENTO DE DATOS', // B3
      'P_inh', // C3
      'I_inh', // D3
      'Riesgo Inherente', // E3
      'Nivel Inherente', // F3
      'Control Preventivo', // G3
      'Efectividad Preventiva', // H3
      'Control Correctivo', // I3
      'Efectividad Correctiva', // J3
      'P_res', // K3
      'I_res', // L3
      'Riesgo Residual', // M3
      'Nivel Residual', // N3
      'Reducción %', // O3
      'Decisión', // P3
      'Plan de Acción', // Q3
      'Responsable', // R3
      'Fecha Objetivo', // S3
      'Estado' // T3
    ]

    // Build data rows from analyses
    const dataRows = analyses.map(analysis => {
      const treatment = analysis.treatment || {}
      const responsible = analysis.responsible || {}

      // Format date correctly - MongoDB dates come as objects with $date property
      let formattedDate = ''
      if (analysis.targetDate) {
        let dateObj
        if (analysis.targetDate.$date) {
          // MongoDB ObjectId date format
          dateObj = new Date(analysis.targetDate.$date)
        } else if (typeof analysis.targetDate === 'string') {
          // ISO string format
          dateObj = new Date(analysis.targetDate)
        } else {
          // Already a Date object
          dateObj = analysis.targetDate
        }

        // Format as DD-MM-YYYY using UTC to avoid timezone issues
        const day = String(dateObj.getUTCDate()).padStart(2, '0')
        const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0')
        const year = dateObj.getUTCFullYear()
        formattedDate = `${day}-${month}-${year}`
      }

      return [
        treatment.treatmentId || '',
        treatment.nombre || '',
        analysis.inheritedP || '',
        analysis.inheritedI || '',
        analysis.inheritedRisk || '',
        analysis.inheritedLevel || '',
        analysis.preventiveControls || '',
        analysis.preventiveEffectiveness || '',
        analysis.correctiveControls || '',
        analysis.correctiveEffectiveness || '',
        analysis.residualP || '',
        analysis.residualI || '',
        analysis.residualRisk || '',
        analysis.residualLevel || '',
        analysis.riskReductionPct !== null ? `${analysis.riskReductionPct}%` : '',
        analysis.decision || '',
        analysis.actionPlan || '',
        responsible.name || '',
        formattedDate,
        analysis.status || ''
      ]
    })

    // Combine all rows
    const wsData = [headerRow1, headerRow2, headerRow3, ...dataRows]

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData)

    // Set column widths
    const colWidths = [
      { wch: 10 },  // ID
      { wch: 40 },  // Tratamiento
      { wch: 8 },   // P_inh
      { wch: 8 },   // I_inh
      { wch: 15 },  // Riesgo Inherente
      { wch: 15 },  // Nivel Inherente
      { wch: 40 },  // Control Preventivo
      { wch: 18 },  // Efectividad Preventiva
      { wch: 40 },  // Control Correctivo
      { wch: 18 },  // Efectividad Correctiva
      { wch: 8 },   // P_res
      { wch: 8 },   // I_res
      { wch: 15 },  // Riesgo Residual
      { wch: 15 },  // Nivel Residual
      { wch: 12 },  // Reducción %
      { wch: 12 },  // Decisión
      { wch: 40 },  // Plan de Acción
      { wch: 25 },  // Responsable
      { wch: 15 },  // Fecha Objetivo
      { wch: 12 }   // Estado
    ]
    ws['!cols'] = colWidths

    // Merge cells for header row 1
    if (!ws['!merges']) ws['!merges'] = []
    ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 19 } }) // A1:T1

    // Merge cells for header row 2
    ws['!merges'].push({ s: { r: 1, c: 0 }, e: { r: 1, c: 1 } })   // A2:B2 (Identificación)
    ws['!merges'].push({ s: { r: 1, c: 2 }, e: { r: 1, c: 5 } })   // C2:F2 (Riesgo Inherente)
    ws['!merges'].push({ s: { r: 1, c: 6 }, e: { r: 1, c: 9 } })   // G2:J2 (Controles)
    ws['!merges'].push({ s: { r: 1, c: 10 }, e: { r: 1, c: 13 } }) // K2:N2 (Riesgo Residual)
    // O2 stands alone (Reducción)
    ws['!merges'].push({ s: { r: 1, c: 15 }, e: { r: 1, c: 19 } }) // P2:T2 (Plan de Acción)

    // Style header rows (bold, centered, background color)
    const headerStyle = {
      font: { bold: true },
      alignment: { horizontal: 'center', vertical: 'center' },
      fill: { fgColor: { rgb: '4472C4' } }
    }

    // Apply styles to header rows
    const headerCells = [
      // Row 1
      ...Array.from({ length: 20 }, (_, i) => XLSX.utils.encode_cell({ r: 0, c: i })),
      // Row 2
      ...Array.from({ length: 20 }, (_, i) => XLSX.utils.encode_cell({ r: 1, c: i })),
      // Row 3
      ...Array.from({ length: 20 }, (_, i) => XLSX.utils.encode_cell({ r: 2, c: i }))
    ]

    headerCells.forEach(cell => {
      if (ws[cell]) {
        ws[cell].s = headerStyle
      }
    })

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'ARR')
  }

  return {
    exportToExcel,
  }
}
